import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axiosJWT from '../../secureAPI/axiosJWT';
import { showToast } from '../../Utils/toastUtils';
import { StartLoading, StopLoading } from '../../Utils/loadingUtils';
import useGetUserData from '../../secureAPI/GetUserData';
import useNavigation from '../../common/useNavigation';
import { useErrorContext } from '../../context/ErrorContext';

const useMidViewProductDetails = () => {
    const { type, id } = useParams();
    const { navigateDashboard } = useNavigation();
    const { users } = useGetUserData();

    // State management
    const [isLoading, setIsLoading] = useState(false);
    const [productDetails, setProductDetails] = useState({
        product: {},
        images: [],
        variants: [],
        documents: [],
        offers: [],
        prizeList: [],
    });
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [mainImage, setMainImage] = useState('');

    // Validate user access
    useEffect(() => {
        if (Array.isArray(users) && users.length > 0) {
            const userTypes = users[0]['UserType'];
            const usertypeArray = typeof userTypes === 'string' ?
                userTypes.split(',').map(str => parseInt(str.trim())).filter(num => !isNaN(num)) : [];

            const hasOne = usertypeArray.includes(1);
            const hasTwo = usertypeArray.includes(2);
            if (!(type === 'seller' && users[0]['IsPersonalDetils'] === 1 && (hasOne && hasTwo))) {
                navigateDashboard('buyer');
            }
        }
    }, [users, type, navigateDashboard]);

    // Fetch product data
    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const details = await getProductDetails(id);
                await getProductOffers(id);

                // Auto-select first variant if available
                if (details?.variants?.length > 0 && !selectedVariant) {
                    setSelectedVariant(details.variants[0]);
                    setMainImage(
                        details.variants[0]?.imageUrl ||
                        (details.images[0]?.url || '')
                    );
                }
            } catch (error) {
                showToast(error.message, 'error');
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [id]);

    const getProductDetails = async (productId) => {
        try {
            const response = await axiosJWT.post(
                `${process.env.REACT_APP_API_URL}/api/Getproductdetails`,
                { product_unique_key: productId },
                { withCredentials: true }
            );

            if (response.data.success === 1) {
                const data = response.data.data;
                const imagesWithUrls = data.images.map(img => ({
                    ...img,
                    url: `${process.env.REACT_APP_API_URL}/api/productsphotos/uploads/${img.image_name}`
                }));

                const variantsWithUrls = data.variants.map(variant => ({
                    ...variant,
                    imageUrl: variant.productPhoto
                        ? `${process.env.REACT_APP_API_URL}/api/products/uploads/${variant.productPhoto}`
                        : null
                }));

                const updatedDetails = {
                    categoryString: response.data.categoryPath,
                    product: data.product,
                    images: imagesWithUrls,
                    variants: variantsWithUrls,
                    documents: data.documents,
                    prizeList: data.priceList
                };

                setProductDetails(updatedDetails);
                return updatedDetails;
            }
            throw new Error(response.data.message || 'Failed to fetch product details');
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to fetch product details');
        }
    };

    const getProductOffers = async (productId) => {
        try {
            const response = await axiosJWT.post(
                `${process.env.REACT_APP_API_URL}/api/Getproductoffers`,
                { product_unique_key: productId },
                { withCredentials: true }
            );

            if (response.data.success === 1) {
                setProductDetails(prev => ({
                    ...prev,
                    offers: response.data.data
                }));
            }
        } catch (error) {
            console.error('Error fetching offers:', error);
        }
    };

    const handleVariantSelect = (variant) => {
        setSelectedVariant(variant);
        setMainImage(variant?.imageUrl ||
            (productDetails.images[0]?.url || ''));
    };

    // console.log(productDetails);
    

    return {
        users,
        productDetails,
        isLoading,
        selectedVariant,
        mainImage,
        handleVariantSelect,
        setMainImage,
        setSelectedVariant // Explicitly expose the setter
    };
};

export default useMidViewProductDetails;